import { 
  Project, 
  Graph, 
  GraphVersion, 
  CreateProjectDto, 
  UpdateProjectDto, 
  CreateGraphDto, 
  UpdateGraphDto 
} from '../types/persistence';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ApiError extends Error {
  constructor(public statusCode: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(
    path: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status, 
        error.error?.message || `Request failed with status ${response.status}`
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Auth
  async getDevToken(): Promise<{ token: string }> {
    return this.request('/auth/dev-token', { method: 'POST' });
  }

  // Projects
  async getProjects(): Promise<{ projects: Project[] }> {
    return this.request('/projects');
  }

  async getProject(id: string): Promise<{ project: Project }> {
    return this.request(`/projects/${id}`);
  }

  async createProject(data: CreateProjectDto): Promise<{ project: Project }> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(id: string, data: UpdateProjectDto): Promise<{ project: Project }> {
    return this.request(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<void> {
    await this.request(`/projects/${id}`, { method: 'DELETE' });
  }

  // Graphs
  async getGraphsByProject(projectId: string): Promise<{ graphs: Graph[] }> {
    return this.request(`/projects/${projectId}/graphs`);
  }

  async getGraph(id: string): Promise<{ graph: Graph }> {
    return this.request(`/graphs/${id}`);
  }

  async createGraph(projectId: string, data: CreateGraphDto): Promise<{ graph: Graph }> {
    return this.request(`/projects/${projectId}/graphs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGraph(id: string, data: UpdateGraphDto): Promise<{ graph: Graph }> {
    return this.request(`/graphs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteGraph(id: string): Promise<void> {
    await this.request(`/graphs/${id}`, { method: 'DELETE' });
  }

  // Versions
  async getGraphVersions(graphId: string): Promise<{ versions: GraphVersion[] }> {
    return this.request(`/graphs/${graphId}/versions`);
  }

  async getGraphVersion(graphId: string, version: number): Promise<{ version: GraphVersion }> {
    return this.request(`/graphs/${graphId}/versions/${version}`);
  }
}

export const apiClient = new ApiClient();
export { ApiError };